import { useRefAsState } from 'modules/React.Extensions'
import { useSelector, useDispatch } from 'react-redux'
import { authFetch } from 'api/auth/authFetch'
import { transformDraftToProgrammeInfo, transformEpisodeDraftToEpisodeInfo } from 'modules/data-entry/AudioVisual/dataTransforms'
import { setProgrammeCreateAuditDataAction, setEpisodeCreateAuditDataAction } from 'components/Body/data-entry/AudioVisual/store/actions'

const createDataByEndpoint = async (endpoint, transformedBody, aggregateVersion, controller, setNewAggregate) => {
    let responseCode = -1

    try {
        const response = await authFetch(endpoint, {
            body: transformedBody ? JSON.stringify(transformedBody) : undefined,
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'AggregateVersion': aggregateVersion
            },
            signal: controller.signal
        })

        if (controller?.signal?.aborted) return null
        responseCode = response.status

        if (responseCode === 201) {
            const aggregateVersionResponse = response.headers.get("aggregateversion")
            setNewAggregate(aggregateVersionResponse)
            const sapCodesResponse = await response.json() // Get the sap code from the response
            return { response, sapCode: sapCodesResponse.sapCode, episodeSapCode: sapCodesResponse.episodeSapCode }
        }

        throw { Message: 'The AV data create was not successful', StatusCode: responseCode, Details: await response.text() }
    } catch (error) {
        throw { Message: 'The AV data create was not successful', StatusCode: responseCode, Details: error }
    }
}

const setUpCreate = (endpoint, transformedBody, aggregateVersion, setNewAggregate) => {
    const controller = new AbortController()
    return {
        controller,
        fetch: () => createDataByEndpoint(endpoint, transformedBody, aggregateVersion, controller, setNewAggregate)
    }
}

const DataCreate = (props) => {
    const [abortController, setAbortController] = useRefAsState(null)
    const { setStatusAction, episodeOnly, requestSelector, setNewProgrammeStatus, setAggregateAction, setEpisodeSapCodeAction, setSapCodeAction } = props;

    const dispatch = useDispatch()
    const setStatus = (newStatus) => dispatch(setStatusAction(newStatus))
    const setNewProgrammeToFalse = () => dispatch(setNewProgrammeStatus(false))
    const setNewAggregate = (newAggregate) => dispatch(setAggregateAction(newAggregate))
    const setUserNameAndDate = (createDate, newUserName) => dispatch(setProgrammeCreateAuditDataAction(createDate, newUserName))
    const setEpisodeUserNameAndDate = (createDate, newUserName) => dispatch(setEpisodeCreateAuditDataAction(createDate, newUserName))

    const setSapCode = (result) => {
        const sapCode = episodeOnly ? result.episodeSapCode : result.sapCode
        dispatch(setEpisodeSapCodeAction({ episodeSapCode: result.episodeSapCode, programmeSapCode: sapCode }))
        if (!episodeOnly) dispatch(setSapCodeAction(sapCode))
    }

    const setUserNamesAndDates = (createDate, newUserName) => {
        setEpisodeUserNameAndDate(createDate, newUserName)
        if (!episodeOnly) {
            setUserNameAndDate(createDate, newUserName)
        }
    }

    const dataCreateRequest = useSelector(requestSelector)
    const { endpoint, status, body, original, episodeDraft, episodeOriginal, aggregateVersion, userName } = dataCreateRequest
    const onComplete = props.onComplete

    const handleCreate = async () => {
        const statusKey = episodeOnly ? 'updateStatus' : 'status'
        setStatus({ [statusKey]: 'pending' })

        if (status === 'pending' && abortController) abortController.abort()

        const transformedBody = episodeOnly
            ? transformEpisodeDraftToEpisodeInfo(episodeOriginal, episodeDraft)
            : transformDraftToProgrammeInfo(body, original, episodeOriginal, episodeDraft, true)

        const { controller, fetch } = setUpCreate(endpoint, transformedBody, aggregateVersion, setNewAggregate)

        setAbortController(controller)

        try {
            const result = await fetch()
            if (result) {
                const currentDate = new Date()
                setUserNamesAndDates(currentDate, userName)
                setSapCode(result)
                setStatus({ [statusKey]: 'complete' })
                if (onComplete) {
                    setTimeout(() => {
                        onComplete()
                        setNewProgrammeToFalse()
                    }, 5000)
                }
            }
        } catch (error) {
            if (!episodeOnly) setSapCode({})
            setStatus({ [statusKey]: 'error', error })
        }
    }

    if (status.updateStatus === 'idle' || status === 'idle') handleCreate()
    return null
}

export default DataCreate